<template>
  <b-container v-if="canCreateStandardPaymentRequest">
    <b-form @submit.prevent="onSubmit" @reset.prevent="reset">
      <PROrderInfo
        :errorMessages="errorMessages.orderInformation"
        v-model="$v.form.orderInformation.$model"
      />

      <OrderDetails
        :errorMessages="errorMessages.orderDetails"
        :size="orderDetailsSize"
        :currencies="enabledCurrencyList"
        v-model="$v.form.orderDetails.$model"
      />

      <StandardInfo
        :errorMessages="errorMessages.paymentRequestInformation"
        v-model="$v.form.paymentRequestInformation.$model"
      />

      <div class="d-flex justify-content-center">
        <b-button id="create-request" type="submit" variant="danger" size="lg"
          >Create Request</b-button
        >
      </div>
    </b-form>
    <LoadingModal v-bind="loadingModal" />
    <AlertModal
      v-bind="alertModal"
      v-model="alertModal.show"
      @dismiss="resetAlert"
    />
    <ConfirmationModal
      v-bind="confirmationModal"
      v-model="confirmationModal.show"
      @dismiss="resetConfirmation"
    />
  </b-container>
</template>

<script>
import { SET_BREADCRUMB } from "@/store/breadcrumbs.module";
import PROrderInfo from "@/components/paymentrequest/PROrderInfo.vue";
import OrderDetails from "@/components/payment/OrderDetails.vue";
import CurrencyMixin from "@/components/payment/CurrencyMixin.js";
import StandardInfo from "@/components/paymentrequest/standard/StandardInfo.vue";
import SPRValidationMixin from "@/components/paymentrequest/StandardPaymentRequestValidationMixin.js";
import AlertModalMixin from "@/components/widgets/AlertModalMixin.js";
import { dateToAWSDate, parseToFloat } from "@/services/Formatting.js";
import {
  getDomain,
  getDefaultReceiptUrl
} from "@/services/MerchantSiteInteractionService.js";
import { API, graphqlOperation } from "aws-amplify";
import PaymentRequestPerimissionsMixin from "@/components/paymentrequest/PaymentRequestPermissionsMixin.js";

const AlertModal = AlertModalMixin.AlertModal;
const LoadingModal = AlertModalMixin.LoadingModal;
const ConfirmationModal = AlertModalMixin.ConfirmationModal;

export default {
  mixins: [
    SPRValidationMixin,
    AlertModalMixin,
    PaymentRequestPerimissionsMixin,
    CurrencyMixin
  ],
  components: {
    PROrderInfo,
    OrderDetails,
    StandardInfo
  },
  data() {
    return {
      form: {
        orderInformation: {
          firstName: "",
          lastName: "",
          email: "",
          orderNumber: ""
        },
        orderDetails: {
          amount: 0,
          currency: ""
        },
        paymentRequestInformation: {
          sendDate: this.setDefaultDate(0),
          expiryDate: this.setDefaultDate(7),
          reason: ""
        }
      },
      orderDetailsSize: {
        currency_sm: 3,
        currencyAmount_sm: 3
      }
    };
  },
  computed: {
    currencyText() {
      return this.getCurrencyTextFromCode(this.form.orderDetails.currency);
    }
  },
  methods: {
    async onSubmit(e) {
      e.preventDefault();
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.alert("Form invalid", AlertModal.WARNING);
        return;
      }

      const confirm = await this.confirm(
        `Create Standard Payment Request?`,
        `Are you sure you want to?`,
        ConfirmationModal.INFORMATION
      );
      if (!confirm) return;
      var alertMessage = "Standard Payment Transaction Requested";
      var alertStatus = AlertModal.SUCCESS;
      try {
        this.startLoadingDialog("Initiating Standard Payment Request");
        const response = await this.createStandardPaymentRequest(this.form);
        if (response) {
          this.reset();
        } else {
          alertMessage = "Failed to Request Standard Payment Transaction";
          alertStatus = AlertModal.ERROR;
        }
      } catch (err) {
        this.$logger.error(err);
        alertMessage =
          "An error occured when attempting to request Standard Payment Transaction";
        alertStatus = AlertModal.ERROR;
      }
      this.stopLoadingDialog(alertMessage, alertStatus);
    },
    reset() {
      this.form.orderInformation = {
        firstName: "",
        lastName: "",
        email: "",
        orderNumber: ""
      };
      this.form.orderDetails = {
        amount: 0,
        currency: this.defaultCurrencyCode
      };
      this.form.paymentRequestInformation = {
        sendDate: this.setDefaultDate(0),
        expiryDate: this.setDefaultDate(7),
        reason: ""
      };
    },
    setDefaultDate(daysInTheFuture) {
      const d = new Date();
      d.setDate(d.getDate() + daysInTheFuture);
      return d;
    },
    getCurrencyTextFromCode(code) {
      const currencyIndex = this.enabledCurrencyList.findIndex(
        c => c.value == code
      );
      return currencyIndex > -1
        ? this.enabledCurrencyList[currencyIndex].text
        : "";
    },
    async createStandardPaymentRequest(form) {
      const createStandardPaymentRequest = `
          mutation CreateStandardPaymentRequest(
            $body: MutationCreateStandardPaymentRequestInput!
          ) {
            createStandardPaymentRequest(body: $body) {
              success
              messageId
              errorMessage
            }
          }
        `;

      const tenantAlias = await this.getMerchantId();
      const domain = getDomain();
      const returnUrl = "";
      const receiptUrl = getDefaultReceiptUrl();

      const standardPaymentRequest = {
        tenantAlias,
        domain,
        returnUrl,
        receiptUrl,
        orderInformation: {
          firstName: form.orderInformation.firstName,
          lastName: form.orderInformation.lastName,
          email: form.orderInformation.email,
          orderNumber: form.orderInformation.orderNumber
        },
        orderDetails: {
          amount: parseToFloat(form.orderDetails.amount),
          currencyCode: form.orderDetails.currency,
          currencyText: this.currencyText
        },
        paymentRequestInformation: {
          sendDate: dateToAWSDate(form.paymentRequestInformation.sendDate),
          expiryDate: dateToAWSDate(form.paymentRequestInformation.expiryDate),
          reason: form.paymentRequestInformation.reason
        }
      };

      try {
        const response = await API.graphql(
          graphqlOperation(createStandardPaymentRequest, {
            body: standardPaymentRequest
          })
        );

        const success = response.data.createStandardPaymentRequest.success;

        if (!success) {
          const errorMessage =
            response.data.createStandardPaymentRequest.errorMessage;
          throw new Error(errorMessage);
        }

        const messageId = response.data.createStandardPaymentRequest.messageId;

        if (!messageId)
          throw new Error("Successful response with no message id");
        return true;
      } catch (e) {
        this.$logger.error(e);
      }
      return false;
    },
    swap() {
      const currency = document.getElementById("currency_select");
      const amount = document.getElementById("currency_input");
      const parent = document.getElementById("currency_parent");

      parent.insertBefore(amount, currency);
    }
  },
  async mounted() {
    await this.retrieveEnabledCurrenciesOnly();

    this.swap();
  }
};
</script>

<style lang="scss" scoped></style>
